import { useCallback, useState } from 'react'
import clsx from 'clsx'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import PageCitation from '@/medical-common/components/PageCitation'
import { selectMedChronReferenceById } from '@/medical-chronology/references/store/selectors'
import ViewSourcePopover from '@/matter-asset-common/references/ViewSourcePopover'
import { useMatterAssetStoreKey } from '@/matter-asset-common/feature/matter-asset-feature-context'
import { selectActiveContentViewerContextMatches } from '@/content-viewer/store/selectors'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'

interface ReferencedSummarySentenceProps {
  referenceId: string
  text: string
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
  isFirst?: boolean
  isCellHovered?: boolean
}

/**
 * Referenced Summary Sentence
 *
 * Displays a single summary sentence item with page citations and “View source” hover behavior.
 * Wraps the sentence text in ViewSourcePopover to enable the popover on:
 * - item-level hover, or
 * - cell-level hover for the first sentence in the cell
 */
export default function ReferencedSummarySentence(props: ReferencedSummarySentenceProps) {
  const { referenceId, text, cellPosition, scrollToCell, isFirst = false, isCellHovered = false } = props
  const medChronJobId = useMatterAssetStoreKey()
  const [isHighlighted, setIsHighlighted] = useState(false)

  const reference = useAppSelector((state: RootState) => selectMedChronReferenceById(state, { medChronJobId, referenceId }))
  // Check if the reference is currently open in the content viewer
  const isActive = useAppSelector((state: RootState) =>
    selectActiveContentViewerContextMatches(state, { kind: ViewerOpenContextKind.MED_CHRON_REFERENCE, referenceId })
  )

  const pages = reference?.pages ?? []
  const hasPages = pages.length > 0
  const textEndsWithPeriod = text.endsWith('.')

  // Memoize the highlight change callback to prevent unnecessary re-renders in ViewSourcePopover
  const handleItemHighlightChange = useCallback((isHighlighted: boolean) => {
    setIsHighlighted(isHighlighted)
  }, [])

  // If no reference found, render static sentence
  if (!reference) {
    return <span className="text-sm inline leading-relaxed px-1 py-0.5 break-words box-decoration-clone">{text}</span>
  }

  // If we have a reference, render the interactive element
  return (
    <ViewSourcePopover
      reference={reference}
      cellPosition={cellPosition}
      scrollToCell={scrollToCell}
      isCellHovered={isCellHovered}
      allowCellHover={isFirst}
      onItemHighlightChange={handleItemHighlightChange}
      isReferenceActive={isActive}
    >
      <div className={clsx('relative group text-sm inline pointer-events-auto', isActive ? 'cursor-default' : 'cursor-pointer')}>
        {/* Text portion with background highlighting */}
        <span
          className={clsx(
            'text-sm rounded-sm px-1 py-0.5 leading-relaxed align-middle inline break-words box-decoration-clone mr-[1px]',
            isHighlighted && 'bg-brand-neutral-100',
            isActive && 'bg-yellow-100'
          )}
        >
          {/* If pages exist, remove period (it goes after citation) - otherwise keep the period here */}
          {hasPages && textEndsWithPeriod ? text.slice(0, -1) : text}
        </span>

        {/* Page citation and period - only render if pages exist */}
        {hasPages && (
          <span className="inline-flex items-center whitespace-nowrap align-middle gap-[1px]">
            <PageCitation pages={pages} highlighted={isActive} />
            {textEndsWithPeriod && <span className="text-sm leading-relaxed mr-[1px]">.</span>}
          </span>
        )}
      </div>
    </ViewSourcePopover>
  )
}
